"use client";

import React from "react";
import { Activity, CircleAlert, CircleCheck, ChevronRight, Cpu, Server, Settings, ShieldCheck, Zap } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const metricsData = [{
  title: "Aktivní systémy",
  value: "1,248",
  change: "+12 % tento měsíc",
  icon: Server,
  status: "normal"
}, {
  title: "Kritická upozornění",
  value: "3",
  change: "Vyžaduje pozornost",
  icon: CircleAlert,
  status: "alert"
}, {
  title: "Prům. účinnost",
  value: "94.2%",
  change: "+1,4 % oproti min. týdnu",
  icon: Zap,
  status: "good"
}];
const performanceData = [{
  time: "00:00",
  efficiency: 92,
  load: 45
}, {
  time: "04:00",
  efficiency: 93,
  load: 42
}, {
  time: "08:00",
  efficiency: 88,
  load: 78
}, {
  time: "12:00",
  efficiency: 85,
  load: 85
}, {
  time: "16:00",
  efficiency: 87,
  load: 82
}, {
  time: "20:00",
  efficiency: 94,
  load: 55
}, {
  time: "24:00",
  efficiency: 95,
  load: 40
}];
const chartConfig = {
  efficiency: {
    label: "Efficiency (%)",
    color: "var(--chart-1)"
  },
  load: {
    label: "System Load",
    color: "var(--chart-2)"
  }
};
const diagnosticLogs = [{
  id: "SYS-092",
  unit: "Chiller Unit A - Roof",
  status: "Optimal",
  temp: "42°F",
  technician: "Alex Johnson"
}, {
  id: "SYS-044",
  unit: "Heat Pump 04 - Zone B",
  status: "Warning",
  temp: "88°F",
  technician: "Sara Lee"
}, {
  id: "SYS-112",
  unit: "RTU-12 - Main Floor",
  status: "Maintenance",
  temp: "72°F",
  technician: "Pending"
}];
export default function TheNexoCorePlatform() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const mockupReveal = useRevealOnScroll({
    threshold: 0.2
  });
  const ctaReveal = useRevealOnScroll({
    threshold: 0.5
  });
  return <section id="the-nexo-core-platform" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--color-primary)_0%,_transparent_50%)] opacity-20" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 max-w-7xl">
        {}
        <div ref={headerReveal.ref} className={`mx-auto max-w-3xl text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary mb-6">
            <Cpu className="w-4 h-4" />
            <span className="text-sm font-semibold tracking-wide uppercase">Vlastní technologie</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6">
            Platforma NEXO CORE
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            NEXO CORE je moderní platforma pro správu servisu vyvinutá pro systémy HVAC. Pomáhá spravovat zařízení, historii servisu, diagnostiku a údržbu s bezprecedentní transparentností.
          </p>
        </div>

        {}
        <div ref={mockupReveal.ref} className={`mx-auto max-w-5xl transition-all duration-1000 delay-200 ${mockupReveal.isVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-12 scale-95"}`}>
          <div className="rounded-xl border border-border/50 bg-card/40 backdrop-blur-xl shadow-2xl overflow-hidden ring-1 ring-white/10">
            {}
            <div className="flex items-center px-4 py-3 border-b border-border/50 bg-muted/30">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-destructive/80" />
                <div className="w-3 h-3 rounded-full bg-secondary/80" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
              </div>
              <div className="mx-auto flex items-center justify-center px-4 py-1 rounded-md bg-background/50 border border-border/50 text-xs text-muted-foreground font-mono w-64">
                <ShieldCheck className="w-3 h-3 mr-2 text-primary" />
                core.nexotechhvac.com
              </div>
            </div>

            {}
            <div className="p-6 md:p-8 grid gap-6">
              {}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {metricsData.map((metric, i) => <Card key={i} data-index={i} className="bg-background/60 border-border/50 shadow-sm">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {metric.title}
                      </CardTitle>
                      <metric.icon className={`w-4 h-4 ${metric.status === "alert" ? "text-destructive" : metric.status === "good" ? "text-emerald-500" : "text-primary"}`} />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold font-mono">{metric.value}</div>
                      <p className="text-xs text-muted-foreground mt-1">{metric.change}</p>
                    </CardContent>
                  </Card>)}
              </div>

              {}
              <Card className="bg-background/60 border-border/50 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Přehled výkonu systému</CardTitle>
                  <CardDescription>Účinnost vs. zatížení v reálném čase u všech monitorovaných jednotek</CardDescription>
                </CardHeader>
                <CardContent>
                  <ChartContainer config={chartConfig} className="h-[250px] w-full">
                    <AreaChart data={performanceData} margin={{
                    top: 10,
                    right: 10,
                    left: -20,
                    bottom: 0
                  }}>
                      <defs>
                        <linearGradient id="fillEfficiency" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-efficiency)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--color-efficiency)" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="fillLoad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-load)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--color-load)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                      <XAxis dataKey="time" tickLine={false} axisLine={false} tickMargin={8} tick={{
                      fill: "var(--muted-foreground)",
                      fontSize: 12
                    }} />
                      <YAxis tickLine={false} axisLine={false} tickMargin={8} tick={{
                      fill: "var(--muted-foreground)",
                      fontSize: 12
                    }} />
                      <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                      <Area type="monotone" dataKey="efficiency" stroke="var(--color-efficiency)" fillOpacity={1} fill="url(#fillEfficiency)" strokeWidth={2} />
                      <Area type="monotone" dataKey="load" stroke="var(--color-load)" fillOpacity={1} fill="url(#fillLoad)" strokeWidth={2} />
                    </AreaChart>
                  </ChartContainer>
                </CardContent>
              </Card>

              {}
              <Card className="bg-background/60 border-border/50 shadow-sm overflow-hidden">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Nedávné diagnostické záznamy</CardTitle>
                    <Button variant="ghost" size="sm" className="text-xs h-8">
                      Zobrazit vše <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader className="bg-muted/30">
                      <TableRow className="border-border/50 hover:bg-transparent">
                        <TableHead className="w-[100px]">ID jednotky</TableHead>
                        <TableHead>Systém</TableHead>
                        <TableHead>Stav</TableHead>
                        <TableHead className="hidden md:table-cell">Teplota</TableHead>
                        <TableHead className="text-right">Technik</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {diagnosticLogs.map((log, i) => <TableRow key={log.id} data-index={i} className="border-border/50">
                          <TableCell className="font-mono text-xs text-muted-foreground">{log.id}</TableCell>
                          <TableCell className="font-medium">{log.unit}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {log.status === "Optimal" && <CircleCheck className="w-4 h-4 text-emerald-500" />}
                              {log.status === "Warning" && <CircleAlert className="w-4 h-4 text-secondary" />}
                              {log.status === "Maintenance" && <Settings className="w-4 h-4 text-muted-foreground" />}
                              <span className="text-sm">{log.status}</span>
                            </div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell font-mono text-sm">{log.temp}</TableCell>
                          <TableCell className="text-right text-sm text-muted-foreground">{log.technician}</TableCell>
                        </TableRow>)}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {}
        <div ref={ctaReveal.ref} className={`mt-16 text-center transition-all duration-1000 delay-400 ${ctaReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Button asChild size="lg" variant="secondary" className="h-14 px-8 text-lg font-semibold shadow-lg shadow-secondary/20 hover:shadow-secondary/40 transition-all hover:-translate-y-1">
            <Link to="#">
              Přihlášení do klientského portálu
              <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
          <p className="mt-4 text-sm text-muted-foreground flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            Zabezpečený přístup k vaší kompletní historii údržby
          </p>
        </div>
      </div>
    </section>;
}